import { useEffect, useMemo, useRef, useState } from "react"
import { Box, Code, Grid, GridItem, HStack, Heading, Link, Spinner, Stack, Text, VStack } from "@chakra-ui/react"
import { useParams, Link as RouterLink } from "react-router-dom"
import SectionCard from "../../framework/SectionCard"
import ErrorAlert from "../../components/ErrorAlert"
import { formatDateTime } from "../../utils/datetime"
import { usePlaceDetail } from "../../hooks/usePlaceDetail"
import { extractErrorInfo } from "../../lib/api/error-utils"

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN

declare global {
  interface Window {
    mapboxgl?: any
  }
}

let mapboxLoader: Promise<any> | null = null

function ensureMapbox() {
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl)
  if (mapboxLoader) return mapboxLoader

  mapboxLoader = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-mapbox-gl]')
    const existingCss = document.querySelector<HTMLLinkElement>('link[data-mapbox-gl-css]')

    if (!existingCss) {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = "https://api.mapbox.com/mapbox-gl-js/v3.1.2/mapbox-gl.css"
      link.dataset.mapboxGlCss = "true"
      document.head.appendChild(link)
    }

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.mapboxgl))
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Mapbox script")))
      return
    }

    const script = document.createElement("script")
    script.src = "https://api.mapbox.com/mapbox-gl-js/v3.1.2/mapbox-gl.js"
    script.async = true
    script.dataset.mapboxGl = "true"
    script.onload = () => resolve(window.mapboxgl)
    script.onerror = () => reject(new Error("Failed to load Mapbox script"))
    document.head.appendChild(script)
  })

  return mapboxLoader
}

function buildStaticMapUrl(lng: number, lat: number) {
  const zoom = 13
  const size = "600x320"
  const marker = `pin-l+285A98(${lng},${lat})`
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${marker}/${lng},${lat},${zoom},0/${size}@2x?access_token=${MAPBOX_TOKEN}`
}

function renderProperties(mergedProps?: Record<string, unknown> | null) {
  if (!mergedProps || Object.keys(mergedProps).length === 0) {
    return <Text color="gray.400">No additional properties</Text>
  }

  return (
    <VStack align="stretch" gap="2">
      {Object.entries(mergedProps).map(([key, value]) => (
        <Box key={key} borderWidth="1px" borderColor="gray.700" borderRadius="md" p="3">
          <Text fontSize="xs" textTransform="uppercase" color="gray.400" letterSpacing="wider">
            {key}
          </Text>
          <Text fontFamily="mono" fontSize="sm" whiteSpace="pre-wrap">
            {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
          </Text>
        </Box>
      ))}
    </VStack>
  )
}

export default function PlaceDetail() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = usePlaceDetail(id)
  const [mapError, setMapError] = useState<string | null>(null)
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<any>(null)

  const mapUrl = useMemo(() => {
    if (!data || !MAPBOX_TOKEN || !Number.isFinite(data.longitude) || !Number.isFinite(data.latitude)) return null
    return buildStaticMapUrl(data.longitude, data.latitude)
  }, [data])

  useEffect(() => {
    if (!data || !mapContainerRef.current) return
    if (!MAPBOX_TOKEN) {
      setMapError("Mapbox token not configured. Set VITE_MAPBOX_PUBLIC_TOKEN to enable maps.")
      return
    }
    if (!Number.isFinite(data.latitude) || !Number.isFinite(data.longitude)) return
    setMapError(null)

    let cancelled = false

    ensureMapbox()
      .then((mapbox) => {
        if (cancelled) return
        mapbox.accessToken = MAPBOX_TOKEN
        const map = new mapbox.Map({
          container: mapContainerRef.current!,
          style: "mapbox://styles/mapbox/streets-v12",
          center: [data.longitude, data.latitude],
          zoom: 13,
        })
        mapInstanceRef.current = map

        if (mapbox.NavigationControl) {
          map.addControl(new mapbox.NavigationControl())
        }
        new mapbox.Marker({ color: "#285A98" }).setLngLat([data.longitude, data.latitude]).addTo(map)
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setMapError(err.message)
        }
      })

    return () => {
      cancelled = true
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [data])

  const errorInfo = error ? extractErrorInfo(error) : null

  return (
    <SectionCard>
      <Stack gap="4">
        <HStack color="gray.400" fontSize="sm">
          <Link asChild color="blue.200">
            <RouterLink to="/places">Places</RouterLink>
          </Link>
          <Text>/</Text>
          <Text>{data?.name ?? id ?? "Place"}</Text>
        </HStack>

        {isLoading && (
          <HStack>
            <Spinner size="sm" />
            <Text>Loading place…</Text>
          </HStack>
        )}

        {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}

        {data && (
          <Stack gap="6">
            <Heading size="md">{data.name || data.id}</Heading>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="6">
              <GridItem>
                <VStack align="stretch" gap="3">
                  <Text><strong>Type:</strong> {data.type || "—"}</Text>
                  <Text><strong>ID:</strong> <Code>{data.id}</Code></Text>
                  <Text>
                    <strong>Coordinates:</strong>{" "}
                    {Number.isFinite(data.latitude) && Number.isFinite(data.longitude)
                      ? `${data.latitude.toFixed(5)}, ${data.longitude.toFixed(5)}`
                      : "—"}
                  </Text>
                  <Text><strong>Updated:</strong> {formatDateTime(data.updatedAt)}</Text>
                  <HStack>
                    <Text fontWeight="semibold">OSM:</Text>
                    {data.osmRef ? (
                      <Link
                        href={data.osmRef.includes("/") ? `https://www.openstreetmap.org/${data.osmRef}` : `https://www.openstreetmap.org/node/${data.osmRef}`}
                        target="_blank"
                        rel="noreferrer"
                        color="blue.200"
                      >
                        {data.osmRef}
                      </Link>
                    ) : (
                      <Text>—</Text>
                    )}
                  </HStack>
                </VStack>
              </GridItem>
              <GridItem>
                {Number.isFinite(data.latitude) && Number.isFinite(data.longitude) && MAPBOX_TOKEN ? (
                  <Box
                    ref={mapContainerRef}
                    borderRadius="lg"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.700"
                    bg="gray.800"
                    minH="280px"
                  />
                ) : mapUrl ? (
                  <Box borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor="gray.700" bg="gray.800">
                    <img src={mapUrl} alt="Map preview" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
                  </Box>
                ) : (
                  <Text color="gray.400">Map preview unavailable</Text>
                )}
                {mapError && <Text color="red.300" fontSize="sm" mt="2">{mapError}</Text>}
              </GridItem>
            </Grid>

            <Box>
              <Heading size="sm" mb="3">
                Merged properties
              </Heading>
              {renderProperties(data.mergedProps)}
            </Box>
          </Stack>
        )}
      </Stack>
    </SectionCard>
  )
}
